import { HeroIcon } from '@components/HeroIcon'
import { Modal } from '@components/Modal'
import { useCallback, useMemo } from 'react'
import { Avatar, XStack, YStack, Text, isWeb } from 'tamagui'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { useSignOut } from '@hooks/useUser'
import { useSession } from '@hooks/useSession'
import { openWebBrowser } from '@utils/openWebBrowser'
import { useDomain } from '@hooks/useThemeParams'

interface IMobileMenuProps {
  open: boolean
  onClose: () => void
}

interface IMenuOptionsProps {
  label: string
  icon: IconName
  url?: string
}

export function MobileMenu({ open, onClose }: IMobileMenuProps) {
  const { user } = useSession()
  const { signOutMutate } = useSignOut()

  const menuOptions: IMenuOptionsProps[] = useMemo(() => {
    if (isWeb)
      return [
        { label: 'Meu Perfil', icon: 'user', url: `/users/${user?.id}/edit` },
        { label: 'Meus pedidos', icon: 'usd-circle', url: '/orders' },
        { label: 'Certificados', icon: 'user-graduate', url: '/certificates' },
        { label: 'Sair', icon: 'door-open' },
      ]

    return [{ label: 'Sair', icon: 'door-open' }]
  }, [user])

  const hasAvatar =
    user?.coverImageUrl !== null &&
    user?.coverImageUrl !== undefined &&
    user?.coverImageUrl !==
      'https://cdn.edools.com/assets/images/users/default.jpeg'

  const handleLogout = useCallback(() => {
    signOutMutate().finally(() => {
      onClose()
    })
  }, [signOutMutate, onClose])

  return (
    <Modal
      onClose={onClose}
      open={open}
      snapPoints={[50]}
      shouldAdapt={true}
      disableDrag={true}
    >
      <YStack
        paddingTop="$spacing-inset-xxl"
        paddingHorizontal="$spacing-inset-sm"
        paddingBottom="$spacing-inset-lg"
      >
        <XStack
          gap="$spacing-inset-sm"
          alignItems="center"
          marginBottom="$spacing-inset-lg"
        >
          <Avatar circular size={40} borderColor="$neutral-600" borderWidth={1}>
            {hasAvatar ? (
              <Avatar.Image src={user?.coverImageUrl} />
            ) : (
              <Avatar.Fallback
                bc="$neutral-600"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  color="$neutral-0"
                  fontSize="$font-size-sm"
                  textAlign="center"
                  fontWeight={700}
                >
                  {user?.firstName?.[0]?.toUpperCase()}
                </Text>
              </Avatar.Fallback>
            )}
          </Avatar>
          <YStack>
            <Text
              color="$neutral-800"
              fontSize="$font-size-xs"
              ff="$mulishBold"
            >
              {user?.firstName} {user?.lastName}
            </Text>
            <Text
              color="$neutral-800"
              fontSize="$font-size-xs"
              ff="$mulishSemiBold"
            >
              {user?.email}
            </Text>
          </YStack>
        </XStack>
        {menuOptions.map((option, index) => (
          <MenuOptions key={index} {...option} handleLogout={handleLogout} />
        ))}
      </YStack>
    </Modal>
  )
}

interface IMenuOptionsProps {
  label: string
  icon: IconName
  url?: string
  handleLogout?: () => void
}

export function MenuOptions({
  label,
  icon,
  url,
  handleLogout,
}: IMenuOptionsProps) {
  const { subdomainURL } = useDomain()

  const handlePress = () => {
    if (!url) return

    openWebBrowser(`${subdomainURL}${url}`)
  }
  return (
    <XStack
      gap="$spacing-inset-nano"
      marginBottom="$spacing-inset-sm"
      onPress={() => (url ? handlePress() : handleLogout?.())}
      role="button"
    >
      <HeroIcon icon={['fal', icon]} color="$neutral-600" size={20} />
      <Text color="$neutral-800" ff="$mulishRegular" fontSize="$font-size-xs">
        {label}
      </Text>
    </XStack>
  )
}
